/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { Alert, AssetDetailModal, SimpleTable, TableSkeleton } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam, dataSourceWireParams } from '@/insights/common/dataSourceParam'
import { calculateTimeDifference } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import {
  ApiService,
  type Asset,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'

import { CONFIG, ISSUES_DISPLAY_ORDER } from './constants'

interface AssetFleetCheckParams extends InsightParametersType {
  companyId: number
  powerDuration: number
  defrostDuration: number
  shutdownPowerDuration: number
  returnAirDiscrepancyHighThresh: number
  returnAirDiscrepancyLowThresh: number
  supplyAirDiscrepancyHighThresh: number
  supplyAirDiscrepancyLowThresh: number
}

interface AssetFleetCheck {
  assetId: number
  assetName: string
  operationalMode: string
  z1Mode: string
  z1SetPoint: number
  z1ReturnAir: number
  z1SupplyAir: number
  discrepancy: number
  reeferOnTime: Date
  location: string
  issue: string
  activeAlarms: string
}

const {
  POWER_DURATION,
  DEFROST_DURATION,
  SHUTDOWN_POWER_DURATION,
  RETURN_AIR_DISCREPANCY_THRESHOLD,
  SUPPLY_AIR_DISCREPANCY_THRESHOLD,
} = CONFIG

const AssetFleetCheck: InsightDefinition<AssetFleetCheckParams, AssetFleetCheck[]> = {
  active: true,
  slug: 'asset/asset-fleet-check',
  category: 'asset',
  name: 'Asset Fleet Check',
  description: 'Check the status of assets in the fleet',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'shutdownPowerDuration',
        control: 'CustomNumberInput',
        defaultValue: SHUTDOWN_POWER_DURATION.default,
        min: SHUTDOWN_POWER_DURATION.min,
        max: SHUTDOWN_POWER_DURATION.max,
        label: 'Shutdown power duration (minutes)',
        showDefault: true,
      },
      {
        id: 'powerDuration',
        control: 'CustomNumberInput',
        defaultValue: POWER_DURATION.default,
        min: POWER_DURATION.min,
        max: POWER_DURATION.max,
        label: 'Power duration (minutes)',
        showDefault: true,
      },
      {
        id: 'returnAirDiscrepancyHighThresh',
        control: 'TemperatureInput',
        defaultValue: RETURN_AIR_DISCREPANCY_THRESHOLD.upper.default,
        min: RETURN_AIR_DISCREPANCY_THRESHOLD.upper.min,
        max: RETURN_AIR_DISCREPANCY_THRESHOLD.upper.max,
        label: 'Return air discrepancy high threshold',
        showDefault: true,
      },
      {
        id: 'returnAirDiscrepancyLowThresh',
        control: 'TemperatureInput',
        defaultValue: RETURN_AIR_DISCREPANCY_THRESHOLD.lower.default,
        min: RETURN_AIR_DISCREPANCY_THRESHOLD.lower.min,
        max: RETURN_AIR_DISCREPANCY_THRESHOLD.lower.max,
        label: 'Return air discrepancy low threshold',
        showDefault: true,
      },
      {
        id: 'supplyAirDiscrepancyHighThresh',
        control: 'TemperatureInput',
        defaultValue: SUPPLY_AIR_DISCREPANCY_THRESHOLD.upper.default,
        min: SUPPLY_AIR_DISCREPANCY_THRESHOLD.upper.min,
        max: SUPPLY_AIR_DISCREPANCY_THRESHOLD.upper.max,
        label: 'Supply air discrepancy high threshold',
        showDefault: true,
      },
      {
        id: 'supplyAirDiscrepancyLowThresh',
        control: 'TemperatureInput',
        defaultValue: SUPPLY_AIR_DISCREPANCY_THRESHOLD.lower.default,
        min: SUPPLY_AIR_DISCREPANCY_THRESHOLD.lower.min,
        max: SUPPLY_AIR_DISCREPANCY_THRESHOLD.lower.max,
        label: 'Supply air discrepancy low threshold',
        showDefault: true,
      },
      {
        id: 'defrostDuration',
        control: 'CustomNumberInput',
        defaultValue: DEFROST_DURATION.default,
        min: DEFROST_DURATION.min,
        max: DEFROST_DURATION.max,
        label: 'Defrost duration (minutes)',
        showDefault: true,
      },
    ],

    isValid: (configuration: AssetFleetCheckParams) => {
      if (configuration.companyId) {
        return true
      }
      return 'Company and Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: AssetFleetCheckParams) => {
    const query = buildQuery({
      companyId: parameters.companyId,
      defrostDuration: parameters.defrostDuration ?? DEFROST_DURATION.default,
      powerDuration: parameters.powerDuration ?? POWER_DURATION.default,
      shutdownPowerDuration: parameters.shutdownPowerDuration ?? SHUTDOWN_POWER_DURATION.default,
      returnAirDiscrepancyHighThresh:
        parameters.returnAirDiscrepancyHighThresh ?? RETURN_AIR_DISCREPANCY_THRESHOLD.upper.default,
      returnAirDiscrepancyLowThresh:
        parameters.returnAirDiscrepancyLowThresh ?? RETURN_AIR_DISCREPANCY_THRESHOLD.lower.default,
      supplyAirDiscrepancyHighThresh:
        parameters.supplyAirDiscrepancyHighThresh ?? SUPPLY_AIR_DISCREPANCY_THRESHOLD.upper.default,
      supplyAirDiscrepancyLowThresh:
        parameters.supplyAirDiscrepancyLowThresh ?? SUPPLY_AIR_DISCREPANCY_THRESHOLD.lower.default,
      ...dataSourceWireParams(parameters),
    })

    const response = await api.get<AssetFleetCheck[]>({
      path: `/insights/asset/fleet?${query}`,
      origin: 'AssetFleet',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data || response.data.length === 0) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: AssetFleetCheckParams, title: string) => {
    const path = '/insights/asset/fleet'
    const params = {
      companyId: parameters.companyId,
      defrostDuration: parameters.defrostDuration ?? DEFROST_DURATION.default,
      powerDuration: parameters.powerDuration ?? POWER_DURATION.default,
      shutdownPowerDuration: parameters.shutdownPowerDuration ?? SHUTDOWN_POWER_DURATION.default,
      returnAirDiscrepancyHighThresh:
        parameters.returnAirDiscrepancyHighThresh ?? RETURN_AIR_DISCREPANCY_THRESHOLD.upper.default,
      returnAirDiscrepancyLowThresh:
        parameters.returnAirDiscrepancyLowThresh ?? RETURN_AIR_DISCREPANCY_THRESHOLD.lower.default,
      supplyAirDiscrepancyHighThresh:
        parameters.supplyAirDiscrepancyHighThresh ?? SUPPLY_AIR_DISCREPANCY_THRESHOLD.upper.default,
      supplyAirDiscrepancyLowThresh:
        parameters.supplyAirDiscrepancyLowThresh ?? SUPPLY_AIR_DISCREPANCY_THRESHOLD.lower.default,
    }
    await downloadCsv(api, path, params, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as AssetFleetCheckParams

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={10} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const body = data
      .slice()
      .sort((a, b) => {
        return (
          (ISSUES_DISPLAY_ORDER[a.issue] ?? Number.MAX_SAFE_INTEGER) -
          (ISSUES_DISPLAY_ORDER[b.issue] ?? Number.MAX_SAFE_INTEGER)
        )
      })
      .map((row, _) => ({
        Asset: (
          <button
            onClick={() => setSelectedAsset(row.assetId)}
            className="text-left block text-blue-500 cursor-pointer"
          >
            {row.assetName}
          </button>
        ),
        'Operational Mode': row.operationalMode,
        'Z1 Mode': row.z1Mode,
        'Z1 Setpoint': celsiusToUser(row.z1SetPoint, 1, units) || '',
        'Z1 Return Air': celsiusToUser(row.z1ReturnAir, 1, units) || '',
        'Z1 Supply Air': celsiusToUser(row.z1SupplyAir, 1, units) || '',
        Discrepancy: celsiusToUser(row.discrepancy, 1, units) || '',
        'Reefer On Time': calculateTimeDifference(row.reeferOnTime, new Date()),
        Location: row.location,
        Issue: row.issue,
        'Active Alarms': row.activeAlarms ? (
          <ol>
            {JSON.parse(row.activeAlarms).map(({ code, description }: { code: string; description: string }) => (
              <li key={code}>
                {code}: {description}
              </li>
            ))}
          </ol>
        ) : (
          ''
        ),
      }))

    const dataItem = body[0]
    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(10)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as AssetFleetCheckParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'AssetFleetCheck#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default AssetFleetCheck
